import React, { useRef, useEffect, useState } from "react";
import api from "../../services/api";

import errors, { IAliancaErrors } from "../../Utils/ExtractErrors";

import { Form, Modal, Button, Grid, Image, Label, DropdownProps } from "semantic-ui-react";
import { toast } from "react-toastify";

import ModalApp from "../../components/ModalApp";
import ImageZoom from "../../components/ImageZoom";
import ModalYesNo from "../../components/ModalYesNo";

import KitRepository, { IKitListModel } from "../../Repositories/KitRepository";

import { eStatusKit, eStatusLiberacao, eTipoKit } from "../../interfaces";

interface State {
    kitId: string;
    apiErrors: IAliancaErrors;
}

interface Props {
    revendedorId: string;
    onSuccess: VoidFunction;
    onClose: VoidFunction;
}

const initialState: State = {
    kitId: "",
    apiErrors: { errors: [] }
}

const VincularKit: React.FC<Props> = ({ revendedorId, onSuccess, onClose }) => {

    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<State>(initialState);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [preview, setPreview] = useState<string[]>([]);
    const [kits, setKits] = useState<IKitListModel[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const load = async () => {
        setLoading(true);

        try {
            const response = await KitRepository.List({
                filter: `StatusKit == ${eStatusKit.Representante} && Tipo == ${eTipoKit.Acerto}`
            });
            setKits(response.list);

        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }

    const onButtonClick = () => {
        if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    useEffect(() => {
        if (selectedFiles.length > 0) {
            let images: string[] = [];

            for (const file of selectedFiles) {
                images.push(URL.createObjectURL(file));
            }

            setPreview(preview => preview.concat(images));

            return () => {
                for (const image of images) { URL.revokeObjectURL(image) }
                setPreview([]);
            }
        }
    }, [selectedFiles])

    const onSelectFile = (e: any) => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedFiles([])
            return

        }

        let files: File[] = [];
        files = files.concat(selectedFiles);

        for (const file of e.target.files) {
            files.push(file);
        }

        setSelectedFiles(files);
    }

    const onRemovePreview = (imageUrl: string, index: number) => {
        const images = preview.filter(x => x !== imageUrl);

        selectedFiles.splice(index, 1);

        URL.revokeObjectURL(imageUrl);
        setPreview(images);
    }

    const handleSubmitForm = async () => {
        const hasKit = state.kitId.length > 0;
        const hasTwoFiles = selectedFiles.length >= 2;
        if (!hasKit) return setState(state => ({ ...state, apiErrors: { errors: [{ id: 1, key: "KitId", message: "É necessário selecionar um kit." }] } }));
        if (!hasTwoFiles) return toast("❌ É necessário inserir no mínimo 2 fotos.", { position: "top-center", type: "error" });
        try {
            setLoading(true);

            await onSuccess();

            const response = await KitRepository.Update({
                id: state.kitId,
                kitPutModel: { revendedorId: revendedorId, liberacao: eStatusLiberacao.Pendente }
            });

            if (response.status === 200) {
                const data = new FormData();

                for (const file of selectedFiles) {
                    data.append("files", file);
                }

                setSelectedFiles([]);
                setPreview([]);

                await api.post(`imagem/${state.kitId}`, data);

                toast(`🎉 ${response.data.message}`, { position: "top-center", type: "success" });
                setOpen(false);
            }
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            setState({ ...state, apiErrors: err.response.data });

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }

    const handleOpen = () => {
        load();

        setOpen(true);
    }

    const handleClose = () => {
        setState({ ...state, apiErrors: { errors: [] } });
        onClose();
        setOpen(false);
        setSelectedFiles([]);
        setPreview([]);
    }

    const hasError = (key: string) => {
        return errors.hasError(state.apiErrors, key);
    }

    const getMessage = (key: string) => {
        return errors.getMessage(state.apiErrors, key);
    }

    const handleOnDropdownChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({
            ...state,
            [name]: value,
            apiErrors: { errors: [] }
        }));
    }

    const editarContent = (
        <Modal.Content>
            <Form>
                <Form.Group widths="equal">
                    <Form.Field width={10} error={hasError("KitId")} required>
                        <Form.Dropdown
                            fluid
                            selection
                            search
                            scrolling
                            name="kitId"
                            noResultsMessage="Nenhum registro encontrado."
                            placeholder="Selecione"
                            label="Kit"
                            value={state.kitId}
                            onChange={handleOnDropdownChange}
                            options={kits.map(kit => { return { key: kit.id, value: kit.id, text: kit.numero } })}
                        />
                        {hasError("KitId") && <Label pointing prompt>{getMessage("KitId")}</Label>}
                    </Form.Field>
                </Form.Group>
                <Form.Field>
                    <p>
                        <strong>
                            FOTOS NECESSÁRIAS:<br /> FOTO DE ROSTO SEGURANDO O KIT, FOTO DA PROMISSÓRIA ASSINADA.
                            </strong>
                    </p>
                    <Button
                        content="Selecionar fotos"
                        icon="images"
                        fluid
                        onClick={onButtonClick}
                        color="black"
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={onSelectFile}
                    />
                </Form.Field>
                <Grid columns={4} doubling>
                    {preview.length > 0 &&
                        preview.map((x, index) =>
                            <Grid.Column key={`image-${index}`}>
                                <Image
                                    fluid
                                    label={<ImageZoom imagePath={x} />}
                                    src={x}
                                />
                                <ModalYesNo
                                    trigger={<Button icon="trash" color="red" attached="bottom" />}
                                    header="Apagar"
                                    content="Deseja realmente apagar esta foto?"
                                    onSuccess={() => onRemovePreview(x, index)}
                                />
                            </Grid.Column>
                        )}
                </Grid>
            </Form>
        </Modal.Content>
    );

    return (
        <ModalApp
            open={open}
            loading={loading}
            handleOpen={handleOpen}
            handleClose={handleClose}
            title="Vincular kit"
            content={editarContent}
            trigger={{
                color: "pink",
                content: "Sim"
            }}
            onSuccess={{ content: "Salvar", func: handleSubmitForm }}
        />
    );
};

export default VincularKit;
